import type { CustomAudiencesApiError } from '../types';

/**
 * The proxima-audiences permission each read needs.
 *
 * The delete preview needs the delete grant, so only a user who can confirm the delete sees the dialog's contents.
 */
export const READ_PERMISSIONS = {
  snapshots: 'read:audienceSnapshot',
  destinations: 'read:audienceDestination',
  proximaAdAccounts: 'read:adAccount',
  deletePreview: 'delete:audienceDefinition'
} as const;

export type ReadResource = keyof typeof READ_PERMISSIONS;

/**
 * The message for a 502 on a read whose service calls Meta while it answers.
 *
 * proxima-audiences sends every 5xx with a generic body, so the status is all there is to go on.
 */
const BAD_GATEWAY_MESSAGES: Partial<Record<ReadResource, string>> = {
  proximaAdAccounts: "their free slots couldn't be read from Meta. Reload the page to try again"
};

/**
 * The message for a failed read, written to follow a "Couldn't load X:" style prefix.
 *
 * - A 403 names the missing permission, since a grant gap is not fixed by retrying.
 * - A 502 uses the read's `BAD_GATEWAY_MESSAGES` entry when it has one.
 * - Anything else is the service's message.
 */
export const loadErrorMessage = (
  error: CustomAudiencesApiError,
  resource: ReadResource
): string => {
  if (error.status === 403)
    return `you don't have ${READ_PERMISSIONS[resource]} access for this brand`;
  const badGateway = error.status === 502 ? BAD_GATEWAY_MESSAGES[resource] : undefined;
  return badGateway ?? error.message;
};

/** A failed read as the store keeps it: the message to show and the HTTP status, null when no response arrived. */
export type LoadError = { message: string; status: number | null };

/**
 * Whether a later poll could plausibly succeed where this failure did.
 *
 * - No response (a timeout, a dropped connection), a 429, and any 5xx are transient.
 * - Anything else, such as a 400, 401, 403, or 404, needs the user or the data to change first.
 */
export const isTransientStatus = (status: number | null): boolean =>
  status == null || status === 429 || status >= 500;

/** The `LoadError` for a failed read, with `loadErrorMessage`'s message. */
export const toLoadError = (error: CustomAudiencesApiError, resource: ReadResource): LoadError => ({
  message: loadErrorMessage(error, resource),
  status: error.status
});
